import { useState } from 'react';
import {
  Box, Stack, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Chip,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  setAgreement, setRvpExtracted, updatePerCourierTc, setRateCardRows, markRateCardCodes,
} from '../../../../redux/pricingWizardSlice';
import {
  parseWorkbookToModel, buildApplyPayload,
} from '../helpers/basePricingTemplate';
import {
  buildWorkbookModelV2, generateWorkbookV2, parseWorkbookV2, workbookFileNameV2,
} from '../helpers/workbookTemplateV2';
import { slabsForTemplate } from '../helpers/rateEngine';
import WizardSection from './WizardSection';

export default function BasePricingUpload() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const wizard = useSelector((s) => s.pricingWizard);
  const blended = wizard.rateCardMode === 'blended';
  const [dragOver, setDragOver] = useState(false);
  const [staged, setStaged] = useState(null); // { payload, covered, summary, warnings, errors } | { error }

  const handleDownload = async () => {
    const model = buildWorkbookModelV2(wizard);
    // Count custom bands too: when EVERY selected parent is a custom-slab parent the
    // default bands are legitimately empty and the workbook ships only "(Custom)"
    // sheets. Guarding on model.bands alone refused to download a perfectly valid file.
    const totalBands = model.bands.length + (model.customBands || []).length;
    if (!totalBands) {
      // Zero columns has three distinct causes and they need different actions —
      // blaming service selection for all of them sent people to a Profile step that
      // was already correctly filled in.
      const noServices = !(wizard.resolvedServices || []).length;
      const noGroups = !(wizard.serviceabilityGroups || []).length;
      enqueueSnackbar(
        noServices
          ? 'Service types have not loaded in this session — open the Profile & Service step once, then return here.'
          : noGroups
            ? 'The courier list has not loaded yet — refresh the page and try again.'
            : 'No courier account codes are ticked for the selected services — pick codes on the Profile & Service step.',
        { variant: 'warning' },
      );
      return;
    }
    const buf = await generateWorkbookV2(wizard);
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = workbookFileNameV2(wizard);
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    const extraSheets = [
      model.rvpEnabled ? 'RVP' : null,
      model.codMode === 'VARIABLE' ? 'COD' : null,
      model.rtoMode === 'VARIABLE' ? 'RTO' : null,
    ].filter(Boolean);
    enqueueSnackbar(
      `Workbook downloaded — Courier pricing (${[...new Set([...model.bands, ...(model.customBands || [])].map((b) => b.band))].join(' / ')})${extraSheets.length ? ' + ' + extraSheets.join(' + ') : ''}. ${blended ? 'Blended: one *ALL* column per band.' : 'One column per courier parent.'}`,
      { variant: 'info' },
    );
  };

  const ingest = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const buf = new Uint8Array(ev.target.result);
        // V2 workbook (single file, banded columns) — falls back to the legacy
        // per-parent-sheet parser so previously downloaded templates keep working.
        const parsed = parseWorkbookV2(buf, wizard) || parseWorkbookToModel(buf, wizard);
        const payload = buildApplyPayload(parsed, wizard);
        // Parents the upload actually writes — for blended this is every courier
        // in the lane, not just the representative sheet parent.
        const fwdParents = [...new Set(payload.rows.map((r) => r.parent))];
        const rvpParents = Object.keys(payload.rvpExtracted.rates);
        const accountCodes = [...new Set(payload.rows.map((r) => r.account_code))].length;
        const pricedRows = payload.rows.filter((r) => r.price != null).length;
        const warnings = [...(parsed.warnings || [])];
        const errors = [...(parsed.errors || [])];
        if (!parsed.combos.length) warnings.push('No known tabs found — is this the downloaded template?');
        if (parsed.missingSheets.length) warnings.push(`Missing sheet/tab(s): ${parsed.missingSheets.join(', ')}.`);
        if (parsed.badHeaderSheets.length) warnings.push(`Unreadable header in: ${parsed.badHeaderSheets.join(', ')} — restore the downloaded header row.`);
        if (parsed.unknownSheets.length) warnings.push(`Ignored unknown tab(s): ${parsed.unknownSheets.join(', ')}.`);
        if (parsed.badPriceCells) warnings.push(`${parsed.badPriceCells} non-numeric price cell(s) treated as blank.`);
        if (parsed.metaSlabTemplate && parsed.metaSlabTemplate !== wizard.rateCard.slabTemplate) {
          warnings.push('This file was downloaded under a different slab template — weight slabs may not match the current rate card.');
        }
        if (payload.rows.length > 0 && pricedRows === 0) warnings.push('Tabs matched but every price cell is blank — the forward rate card will not be touched.');
        const blankTabs = parsed.combos
          .filter((c) => c.kind === 'forward' && !c.rows.some((r) => r.price != null))
          .map((c) => `${c.parent}-${c.serviceId}`);
        if (blankTabs.length && pricedRows > 0) {
          warnings.push(`Column(s)/tab(s) with no priced rows: ${blankTabs.join(', ')} — their existing prices will be cleared on apply.`);
        }
        setStaged({
          payload,
          covered: payload.covered,
          summary: {
            tabs: parsed.combos.length, fwdParents, rvpParents, accountCodes,
            rows: payload.rows.length, pricedRows, slabs: slabsForTemplate(wizard).length,
          },
          warnings,
          errors,
        });
      } catch (e) {
        setStaged({ error: (e && e.message) || 'Could not read this file — please use the downloaded template.' });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const apply = () => {
    if (!staged || staged.error) return;
    const { payload, covered, summary } = staged;
    // Forward rows apply only when at least one forward price was filled —
    // an RVP-only upload must not blank the existing forward rate card.
    if (summary.pricedRows > 0) {
      dispatch(setAgreement({ extracted: payload.forwardExtracted }));
      // Combos whose tab was deleted from the file keep their existing rows
      // (matches the "those combos stay unchanged" warning).
      const coveredSet = new Set(covered);
      const kept = wizard.rateCard.rows.filter((r) => !coveredSet.has(`${r.parent}|${r.service}`));
      dispatch(setRateCardRows({ rows: [...kept, ...payload.rows], source: 'template' }));
      // Replaced prices need a fresh parent-level confirmation pass.
      dispatch(markRateCardCodes({ codes: [...new Set(payload.rows.map((r) => r.account_code))], status: 'in-progress' }));
    }
    if (Object.keys(payload.rvpExtracted.rates).length) dispatch(setRvpExtracted(payload.rvpExtracted));
    Object.entries(payload.perCourierTc).forEach(([parent, patch]) => dispatch(updatePerCourierTc({ parent, patch })));
    enqueueSnackbar(`Applied · ${summary.accountCodes} account code(s) populated from ${summary.tabs} tab(s)`, { variant: 'success' });
    setStaged(null);
  };

  const canApply = staged && !staged.error
    && !(staged.errors && staged.errors.length)
    && (staged.summary.pricedRows > 0 || staged.summary.rvpParents.length > 0);

  return (
    <Box sx={{ mb: 1.5 }}>
      <WizardSection
        icon="📥"
        title="Rate card workbook (Forward + RVP)"
        description={blended
          ? 'One workbook: Courier pricing (one *ALL* column per band — Surface / Air / SDD / NDD), plus RVP / COD / RTO sheets as configured. Fill prices once; upload applies them to every selected courier account code.'
          : 'One workbook: Courier pricing with one column per courier parent, grouped Surface / Air / SDD / NDD, plus RVP / COD / RTO sheets as configured. Fill prices, then upload — values fan out to every selected account code.'}
        badge="required"
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <Button variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={handleDownload}
            sx={{ borderRadius: '8px', fontWeight: 600, flexShrink: 0 }}>
            Download template
          </Button>
          <Box
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); ingest(e.dataTransfer.files && e.dataTransfer.files[0]); }}
            sx={{
              flex: 1, width: '100%', px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1,
              border: '1.5px dashed', borderRadius: 1.5,
              borderColor: dragOver ? 'primary.main' : 'divider',
              bgcolor: dragOver ? 'primary.lighter' : 'background.neutral',
              transition: 'all .15s',
            }}
          >
            <CloudUploadOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>
              Drag the filled template here, or{' '}
              <Box component="label" sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}>
                browse
                <input hidden type="file" accept=".xlsx,.xls" onChange={(e) => ingest(e.target.files && e.target.files[0])} />
              </Box>
            </Typography>
          </Box>
        </Stack>
      </WizardSection>

      <Dialog open={Boolean(staged)} onClose={() => setStaged(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Preview base-pricing upload</DialogTitle>
        <DialogContent dividers>
          {staged && staged.error ? (
            <Alert severity="error">{staged.error}</Alert>
          ) : staged ? (
            <Stack spacing={1.5}>
              <Typography variant="body2">This will populate the rate card from the uploaded tabs:</Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                <Chip size="small" color="primary" variant="outlined" label={`${staged.summary.tabs} tab(s)`} />
                <Chip size="small" variant="outlined" label={`${staged.summary.fwdParents.length} forward parent(s)`} />
                {staged.summary.rvpParents.length > 0 && (
                  <Chip size="small" color="secondary" variant="outlined" label={`${staged.summary.rvpParents.length} RVP parent(s)`} />
                )}
                <Chip size="small" label={`${staged.summary.accountCodes} account codes`} />
                <Chip size="small" label={`${staged.summary.pricedRows}/${staged.summary.rows} rows priced`} />
                <Chip size="small" label={`${staged.summary.slabs} slabs`} />
              </Stack>
              {staged.summary.fwdParents.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>Forward parents</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {staged.summary.fwdParents.map((p) => <Chip key={p} size="small" label={p} sx={{ bgcolor: 'background.neutral' }} />)}
                  </Stack>
                </Box>
              )}
              {(staged.errors || []).map((e, i) => <Alert key={`e${i}`} severity="error" sx={{ py: 0.25 }}>{e}</Alert>)}
              {(staged.warnings || []).map((w, i) => <Alert key={i} severity="warning" sx={{ py: 0.25 }}>{w}</Alert>)}
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Existing rate-card rows will be replaced. You can still edit individual account codes afterward.
              </Typography>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setStaged(null)}>Cancel</Button>
          <Button variant="contained" onClick={apply} disabled={!canApply}>
            Apply to rate card
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
